import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Table, InputNumber, Select, Button, Popconfirm, Card, Tooltip } from 'antd';
import { UserAddOutlined, PlusOutlined, DeleteOutlined, InfoCircleOutlined, CopyOutlined, HolderOutlined } from '@ant-design/icons';
import { ParticipantsAndRatesTable } from '../interfaces/interfaces';
import { getPricingCalculatorDetails } from '../../services/Apis';
import FilterFields from '../hooks/FilterFields';
import { getTooltip } from '../services/tooltip_services';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ColumnsType } from 'antd/es/table';
import { useCurrency } from '../../context/CurrencyContext';
import { mapDsxLevel } from '../../utils/utils';

const { Option } = Select;
interface ParticipantsAndRatesProps {
  dataSource: ParticipantsAndRatesTable[];
  onChange: (data: ParticipantsAndRatesTable[]) => void;
  pricingCalculatorDetailsData: any;
  viewOnly?: boolean;
  pipelineName?: string;
  hoveredRowKey?: number | null;
  onRowHover?: (key: number | null) => void;
  onCopyResources?: () => void;
  onSearchEmployees?: () => void;
}

const transformData_ = (data: any): Record<string, any> => {
  if (!data) return {};
  const groupedData: Record<string, any> = {};

  data.forEach((item: { resource_type: string, region: string, service_line: string, function: string, career_level: string }) => {
    const { resource_type, region, service_line, function: func, career_level } = item;

    // Initialize the resource_type if it doesn't exist
    if (!groupedData[resource_type]) {
      groupedData[resource_type] = {};
    }

    if (!groupedData[resource_type][region]) {
      groupedData[resource_type][region] = {};
    }
    if (!groupedData[resource_type][region][service_line]) {
      groupedData[resource_type][region][service_line] = {};
    }

    if (!groupedData[resource_type][region][service_line][func]) {
      groupedData[resource_type][region][service_line][func] = []
    }

    if (!groupedData[resource_type][region][service_line][func].includes(career_level)) {
      groupedData[resource_type][region][service_line][func].push(career_level);
    }
  });

  return groupedData;
};

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const SortableRow: React.FC<RowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 1 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

// Ant Design also uses the body row component for its keyless empty-state row.
const Row: React.FC<RowProps> = (props) => props['data-row-key'] == null
  ? <tr {...props} /> : <SortableRow {...props} />;

const ParticipantsAndRates: React.FC<ParticipantsAndRatesProps> = ({ dataSource: initialData, onChange, pricingCalculatorDetailsData, viewOnly = false, pipelineName = '', hoveredRowKey, onRowHover, onCopyResources, onSearchEmployees }) => {
  const { dealCurrency, currencySymbol } = useCurrency();
  const suppressBlurRef = useRef(false);
  const [dataSource, setDataSource] = useState<ParticipantsAndRatesTable[]>(initialData);
  const [options_, setOptions_] = useState(transformData_(pricingCalculatorDetailsData));
  const [prevStateData, setPrevStateData] = useState<ParticipantsAndRatesTable[]>([]);
  const [selectedPipelineName, setSelectedPipelineName] = useState<string>(pipelineName);
  const mapOptions = (fieldAttribute?: string[] | undefined) => {
    return fieldAttribute ? fieldAttribute.map(item => item).sort() : []
  }

  // Order the fieldAttribute based on fieldAttributeId number
  const mapOptionsOrderWithId = (fieldAttribute?: string[] | undefined, fieldAttributeId?: number[] | undefined) => {
    let orderedFieldAttribute: string[] = [];
    if (fieldAttribute && fieldAttributeId) {
      let s: [string, number][] = fieldAttribute.map((item, index) => [item, fieldAttributeId[index]]);
      s.sort((x, y) => x[1] - y[1]);
      orderedFieldAttribute = s.map(item => item[0]);
    }
    return orderedFieldAttribute;
  }

  useEffect(() => {
    setDataSource(initialData);
  }, [initialData]);

  useEffect(() => {
    setOptions_(transformData_(pricingCalculatorDetailsData));
  }, [pricingCalculatorDetailsData]);

  useEffect(() => {
    const isAllFieldsPopulated = dataSource.every(
      (item) =>
        item.resourceType &&
        item.selectRegion &&
        item.serviceLineCapability &&
        item.function &&
        item.selectLevel
    );

    if (isAllFieldsPopulated) {
      onChange(dataSource);
    } else {
      const levelChanged = dataSource.some((resource, index) => resource.selectLevel !== prevStateData[index]?.selectLevel);
      if (levelChanged) {
        // Filter out rows with any null, undefined, or '' value
        const cleanedData = dataSource.filter(row =>
          Object.values(row).every(value => value !== null && value !== undefined && value !== '')
        );
        onChange(cleanedData);
      }
    }
  }, [dataSource]);

  const filteredValues: any = FilterFields(pricingCalculatorDetailsData, dataSource.map((item) => ({
    resource_type: item.resourceType ?? '',
    resource_type_id: '',
    region: item.selectRegion ?? '',
    region_id: '',
    service_line: item.serviceLineCapability ?? '',
    service_line_id: '',
    career_level: item.selectLevel ?? '',
    career_level_id: '',
    function: item.function ?? '',
    function_id: '',
    cost_per_hour_editable: '',
    target_client_margin_editable: '',
  })));

  const columns: ColumnsType<ParticipantsAndRatesTable> = [
    {
      title: '',
      key: 'sort',
      align: 'center' as const,
      width: 80,
      render: () => <DragHandle />,
    },
    {
      title: '',
      key: 'rowNumber',
      align: 'center' as const,
      width: 48,
      render: (_: any, __: ParticipantsAndRatesTable, index: number) => (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 28,
            height: 28,
            padding: '0 8px',
            borderRadius: 6,
            border: '1px solid #d6e4ff',
            backgroundColor: '#f0f5ff',
            color: '#4096FF',
            fontSize: 12,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
          }}
        >
          {index + 1}
        </span>
      ),
    },
    {
      dataIndex: 'action',
      render: (_: any, record: ParticipantsAndRatesTable) =>
        dataSource.length >= 1 ? (
          <div style={{ width: "75px" }}>
            <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record.key)}>
              <Button icon={<DeleteOutlined color='#F3797E' />} style={{ color: '#F3797E' }} disabled={viewOnly} />
            </Popconfirm>
            <Button className='ms-2' onClick={() => handleDuplicate(record)} disabled={viewOnly} icon={<CopyOutlined color='#6FBE73' />} style={{ color: '#6FBE73' }} />
          </div>
        ) : null,
    },
    {
      title: (
        <span>
          Resource Type
          <Tooltip title={<span dangerouslySetInnerHTML={{ __html: getTooltip("Resource Type") || '' }} />}>
            <InfoCircleOutlined style={{ marginLeft: "10px", color: "#4096FF" }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'resourceType',
      minWidth: 100,
      render: (text: string, record: ParticipantsAndRatesTable) => (
        filteredValues.length == dataSource.length ? (
          <Select allowClear={true} value={record.resourceType} defaultValue={record.resourceType} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'resourceType')} disabled={viewOnly}>
            {Object.keys(options_).map((item: string) => {
              return <Option key={`${record.key}-${item}-rt`} value={item}>{item}</Option>
            })}
          </Select>
        ) : <Select allowClear={true} value={record.resourceType} defaultValue={record.resourceType} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'function')} disabled={viewOnly}>
        </Select>
      ),
    },
    {
      title: 'Region',
      dataIndex: 'selectRegion',
      render: (text: string, record: ParticipantsAndRatesTable) => (
        filteredValues.length == dataSource.length ? (
          <Select allowClear={true} value={record.selectRegion} defaultValue={record.selectRegion} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'selectRegion')} disabled={viewOnly || (record.resourceType === undefined || record.resourceType == '')} >
            {(record.resourceType != undefined && record.resourceType !== '' && options_[record.resourceType]) ? Object.keys(options_[record.resourceType]).map((item: string) => {
              return <Option key={`${record.key}-${item}-sr`} value={item}>{item}</Option>
            }) : []}
          </Select>
        ) : <Select allowClear={true} value={record.selectRegion} defaultValue={record.selectRegion} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'function')} disabled={viewOnly || (record.resourceType === undefined || record.resourceType == '')}>
        </Select>
      ),
    },
    {
      title: (
        <span>
          Service Line
          <Tooltip title={<span dangerouslySetInnerHTML={{ __html: getTooltip("Service Line") || '' }} />}>
            <InfoCircleOutlined style={{ marginLeft: "10px", color: "#4096FF" }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'serviceLineCapability',
      render: (text: string, record: ParticipantsAndRatesTable) => (
        filteredValues.length == dataSource.length ? (
          <Select allowClear={true} value={record.serviceLineCapability} defaultValue={record.serviceLineCapability} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'serviceLineCapability')}
            disabled={viewOnly || (!record.selectRegion || !record.resourceType)}
          >
            {(record.selectRegion != undefined && record.selectRegion !== '' && options_[record.resourceType]?.[record.selectRegion]) ? Object.keys(options_[record.resourceType][record.selectRegion]).sort().map((item: string) => {
              return <Option key={`${record.key}-${item}-sl`} value={item}>{item}</Option>
            }) : []}
          </Select>
        ) : <Select allowClear={true} value={record.serviceLineCapability} defaultValue={record.serviceLineCapability} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'function')} disabled={viewOnly || (!record.selectRegion || !record.resourceType)}>
        </Select>
      ),
    },
    {
      title: 'Function',
      dataIndex: 'function',
      render: (text: string, record: ParticipantsAndRatesTable) => (
        filteredValues.length == dataSource.length ? (
          <Select allowClear={true} value={record.function} defaultValue={record.function} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'function')}
            disabled={viewOnly || (!record.serviceLineCapability || !record.selectRegion || !record.resourceType)}
          >
            {(record.serviceLineCapability != undefined && record.serviceLineCapability !== '' && options_[record.resourceType]?.[record.selectRegion]?.[record.serviceLineCapability]) ? Object.keys(options_[record.resourceType][record.selectRegion][record.serviceLineCapability]).map((item: string) => {
              return <Option key={`${record.key}-${item}-fn`} value={item}>{item}</Option>
            }) : []}
          </Select>
        ) : <Select allowClear={true} value={record.function} defaultValue={record.function} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'function')} disabled={viewOnly || (!record.serviceLineCapability || !record.selectRegion || !record.resourceType)}>
        </Select>
      ),
    },
    {
      title: 'Level',
      dataIndex: 'selectLevel',
      render: (text: string, record: ParticipantsAndRatesTable) => (
        filteredValues.length == dataSource.length ? (
          <Select allowClear={true} value={record.selectLevel} defaultValue={record.selectLevel} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'selectLevel')}
            disabled={viewOnly || (!record.serviceLineCapability || !record.selectRegion || !record.resourceType || !record.function)}
          >
            {(record.function != undefined && record.function !== '' && options_[record.resourceType]?.[record.selectRegion]?.[record.serviceLineCapability]?.[record.function]) ?
              options_[record.resourceType][record.selectRegion][record.serviceLineCapability][record.function].map((item: string) => {
                return <Option key={`${record.key}-${item}-lv`} value={item}>{selectedPipelineName == "EMEA Pipeline" ? item + " - " + mapDsxLevel(item): item}</Option>
              }) : []}
          </Select>
        ) : <Select allowClear={true} value={record.selectLevel} defaultValue={record.selectLevel} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'function')} disabled={viewOnly || (!record.serviceLineCapability || !record.selectRegion || !record.resourceType || !record.function)}>
        </Select>
      ),
    },
    {
      title: (
        <span>
          Loaded Labor Cost/Hr
          <Tooltip title={<span dangerouslySetInnerHTML={{ __html: getTooltip("Loaded Labor Cost/Hr") || '' }} />}>
            <InfoCircleOutlined style={{ marginLeft: "10px", color: "#4096FF" }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'loadedLaborCostHr',
      render: (text: number, record: ParticipantsAndRatesTable) =>
        <div style={{ position: 'relative', width: '100%' }}>
          <InputNumber
            prefix={`${currencySymbol}`}
            value={record.loadedLaborCostHr}
            onChange={(value) => handleInputChange(value, record.key, 'loadedLaborCostHr')}
            disabled={
              viewOnly ? true : filteredValues.length == dataSource.length ?
                (filteredValues[record.key - 1].cost_per_hour_editable?.length === 1 && filteredValues[record.key - 1].cost_per_hour_editable[0] == true) ? false : true
                : true}
            style={{ width: 100 }}
            className="bold-input" />
        </div>
    },
    {
      title: 'Target Client Margin %',
      dataIndex: 'targetClientMargin',
      render: (text: number, record: ParticipantsAndRatesTable) =>
        <div style={{ position: 'relative', width: '100%' }}>
          <InputNumber
            formatter={(value) => ` ${value} %`}
            value={record.targetClientMargin}
            onChange={(value) => handleInputChange(value, record.key, 'targetClientMargin')}
            disabled={
              viewOnly ? true : filteredValues.length == dataSource.length ?
                (filteredValues[record.key - 1].target_client_margin_editable?.length === 1 && filteredValues[record.key - 1].target_client_margin_editable[0] == true) ? false : true
                : true}
            style={{ width: 100 }}
            className="bold-input" />
        </div>
    },
    {
      title: 'Target Price/Hr',
      dataIndex: 'targetPriceHr',
      render: (text: number) =>
        <div style={{ position: 'relative', width: '100%' }}>
          <InputNumber prefix={`${currencySymbol}`} value={Number(text?.toFixed(2))} disabled style={{ width: 100 }} className="bold-input" />
        </div>
    },
    {
      title: 'Input Proposed Price / Hour',
      dataIndex: 'proposedPriceHr',
      render: (text: number, record: ParticipantsAndRatesTable) => (
        <div style={{ position: 'relative', width: '100%' }}>
          <InputNumber
            min={0}
            value={text}
            prefix={`${currencySymbol}`}
            onChange={(value) => handleInputChange(value, record.key, 'proposedPriceHr')}
            style={{ width: 100, borderColor: 'blue' }}
            className="bold-input"
            disabled={viewOnly}
          />
        </div>
      ),
    },
    {
      title: 'Client Margin at Proposed Price/Hr',
      dataIndex: 'clientMarginProposed',
      render: (text: number) =>
        <div style={{ position: 'relative', width: '100%' }}>
          <InputNumber formatter={(value) => ` ${value} %`} value={Number(text?.toFixed(1))} disabled style={{ width: 100 }} className="bold-input" />
        </div>
    },
    {
      title: 'Variance to Target Client Margin %',
      dataIndex: 'varianceTargetMargin',
      render: (text: number) =>
        <div style={{ position: 'relative', width: '100%' }}>
          <InputNumber formatter={(value) => ` ${value} %`} value={Number(text?.toFixed(1))} disabled style={{ width: 100, borderColor: Number(text.toFixed(1)) < 0 ? '#F3797E' : '#6FBE73', color: Number(text.toFixed(1)) < 0 ? '#F3797E' : '#6FBE73' }} className="bold-input" />
        </div>
    },
  ];

  const handleDelete = (key: number) => {
    const filteredData = [...dataSource.filter((item) => item.key !== key)];
    const newData = filteredData.map((item, index) => ({ ...item, "key": index + 1 }))
    setDataSource(newData);
  };

  const handleAdd = () => {
    // Find the maximum key in the current data source to ensure the new key is unique
    const maxKey = dataSource.length > 0 ? Math.max(...dataSource.map(item => item.key)) : 0;
    const oldMaxKey = dataSource.length > 0 ? Math.max(...dataSource.map(item => item.oldKey)) : 0;

    const newData: ParticipantsAndRatesTable = {
      key: maxKey + 1,  // Use the max key + 1 to ensure uniqueness
      oldKey: oldMaxKey + 1,
      resourceType: 'PERM',
      selectRegion: '',
      serviceLineCapability: '',
      function: '',
      selectLevel: '',
      loadedLaborCostHr: 0,
      targetClientMargin: 0,
      targetPriceHr: 0,
      proposedPriceHr: 0,
      clientMarginProposed: 0,
      varianceTargetMargin: 0,
      minFTE: 0,
    };

    setDataSource([...dataSource, newData]);
  };

  const handleDuplicate = (duplicateRow: any) => {
    // Find the maximum key in the current data source to ensure the new key is unique
    const maxKey = dataSource.length > 0 ? Math.max(...dataSource.map(item => item.key)) : 0;
    const oldMaxKey = dataSource.length > 0 ? Math.max(...dataSource.map(item => item.oldKey)) : 0;

    const newData: ParticipantsAndRatesTable = {
      key: maxKey + 1,  // Use the max key + 1 to ensure uniqueness
      oldKey: oldMaxKey + 1,
      resourceType: duplicateRow.resourceType,
      selectRegion: duplicateRow.selectRegion,
      serviceLineCapability: duplicateRow.serviceLineCapability,
      function: duplicateRow.function,
      selectLevel: duplicateRow.selectLevel,
      loadedLaborCostHr: duplicateRow.loadedLaborCostHr,
      targetClientMargin: duplicateRow.targetClientMargin,
      targetPriceHr: duplicateRow.targetPriceHr,
      proposedPriceHr: duplicateRow.proposedPriceHr,
      clientMarginProposed: duplicateRow.clientMarginProposed,
      varianceTargetMargin: duplicateRow.varianceTargetMargin,
      minFTE: duplicateRow.minFTE,
    };

    setDataSource([...dataSource, newData]);
  };

  const handleSelectChange = (value: string, key: number, column: string) => {
    setPrevStateData([...dataSource])
    const order_ = ['resourceType', 'selectRegion', 'serviceLineCapability', 'function', 'selectLevel']
    const order_indx = order_.indexOf(column)
    let clear_values = {}
    for (let i = order_indx; i < order_.length; i++) {
      clear_values = { ...clear_values, [order_[i]]: '' }
    }
    // clearValues(key, column)

    const newData = [...dataSource];
    const index = newData.findIndex((item) => key === item.key);
    if (index > -1) {
      const item = { ...newData[index], ...clear_values, [column]: value };
      if (item.resourceType &&
        item.selectRegion &&
        item.serviceLineCapability &&
        item.function &&
        item.selectLevel) {
        const lookUpValues = pricingCalculatorDetailsData.filter((data: any) => data.resource_type === item.resourceType && data.region === item.selectRegion && data.service_line === item.serviceLineCapability && data.career_level === item.selectLevel && data.function === item.function)[0]
        item.loadedLaborCostHr = lookUpValues?.cost_per_hour;
        item.targetClientMargin = Number((lookUpValues?.target_client_margin * 100).toFixed(2));
        item.targetPriceHr = Number(((item.loadedLaborCostHr * 100) / (100 - item.targetClientMargin)).toFixed(2));
        item.proposedPriceHr = Number(((item.loadedLaborCostHr * 100) / (100 - item.targetClientMargin)).toFixed(2));
        item.clientMarginProposed = Number((100 - ((100 * item.loadedLaborCostHr) / (item.proposedPriceHr))).toFixed(2));
        item.varianceTargetMargin = Number((item.clientMarginProposed - item.targetClientMargin).toFixed(2));
        item.minFTE = lookUpValues?.min_fte_allocation ? lookUpValues?.min_fte_allocation : 0;
      } else {
        item.loadedLaborCostHr = 0;
        item.targetClientMargin = 0;
        item.targetPriceHr = 0;
        item.proposedPriceHr = 0;
        item.clientMarginProposed = 0;
        item.varianceTargetMargin = 0;
        item.minFTE = 0;
      }
      newData.splice(index, 1, item);
      setDataSource(newData);
    }
  };

  const handleInputChange = (value: number | null, key: number, column: string) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => key === item.key);
    if (index > -1) {
      const item = { ...newData[index], [column]: value || 0 };
      item.targetPriceHr = Number(((item.loadedLaborCostHr * 100) / (100 - item.targetClientMargin)).toFixed(2));
      if (column == 'loadedLaborCostHr' || column == 'targetClientMargin')
        item.proposedPriceHr = Number(((item.loadedLaborCostHr * 100) / (100 - item.targetClientMargin)).toFixed(2))
      item.clientMarginProposed = item.proposedPriceHr != 0 ? Number((100 - ((100 * item.loadedLaborCostHr) / (item.proposedPriceHr))).toFixed(2)) : 0;
      item.varianceTargetMargin = Number((item.clientMarginProposed - item.targetClientMargin).toFixed(2));
      newData.splice(index, 1, item);
      setDataSource(newData);
    }
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      suppressBlurRef.current = true;
      const overIndex = dataSource.findIndex((record) => record.oldKey === over?.id);
      setDataSource((prevState) => {
        const activeIndex = prevState.findIndex((record) => record.oldKey === active?.id);
        const newData = arrayMove(prevState, activeIndex, overIndex);
        const updatedData = newData.map((item, index) => ({ ...item, key: index + 1 }));
        return updatedData;
      });
      onRowHover?.(overIndex + 1);
      setTimeout(() => { suppressBlurRef.current = false; }, 0);
    }
  };

  return (
    <Card title={<>Resources and Rates <Tooltip title='Add a custom resource or search employees to fill their role and catalog rates'><InfoCircleOutlined style={{ marginLeft: "10px", marginTop: "2px", color: "#4096FF" }} /></Tooltip></>}
      extra={!viewOnly && onCopyResources && <Button size="small" icon={<CopyOutlined />} onClick={onCopyResources}>Copy resources from deal</Button>}
      style={{ marginBottom: '16px', boxShadow: "#8888881c 0px 0px 7px 7px", border: "2px solid #80808066"}}>
      <div style={{ width: '100%' }}>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext items={dataSource.map((i) => i.oldKey)} strategy={verticalListSortingStrategy}>
            <Table
              dataSource={dataSource}
              columns={columns}
              tableLayout='auto'
              pagination={false}
              rowKey="oldKey"
              size="small"
              scroll={{ x: '100%' }}
              style={{ marginBottom: '12px' }}
              components={{ body: { row: Row } }}
              rowClassName={(record: ParticipantsAndRatesTable) =>
                record.key === hoveredRowKey ? 'row-highlighted' : ''
              }
              onRow={(record: ParticipantsAndRatesTable) => ({
                onFocus: () => onRowHover?.(record.key),
                onBlur: (e: React.FocusEvent<HTMLElement>) => {
                  if (suppressBlurRef.current) return;
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    onRowHover?.(null);
                  }
                },
              })}
            />
          </SortableContext>
        </DndContext>
        <div role="group" aria-label="Add resources" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={viewOnly}
            style={{ height: 32, paddingInline: 12, boxShadow: 'none' }}>Add blank row</Button>
          {!viewOnly && onSearchEmployees && <Button icon={<UserAddOutlined />} onClick={onSearchEmployees}
            style={{ height: 32, paddingInline: 12, boxShadow: 'none' }}>Select resources</Button>}
        </div>
      </div>
    </Card>
  );
};

export default ParticipantsAndRates;
